/**
 * Outil « Pourcentages et évolutions » — calculs exacts (pur).
 *
 * Les taux sont des pourcentages ENTIERS ; une valeur de départ entière.
 * Coefficient multiplicateur d'une évolution de t % : 1 + t/100 ; d'une
 * suite d'évolutions : le produit ; taux global : (CM − 1) × 100 % ; taux
 * réciproque (pour revenir au départ) : (1/CM − 1) × 100 %. Tout en
 * fractions, pour écrire 5/6 et non 0,8333.
 */

import { div, exact, frac, mul, sub, type ExactNumber, type Frac } from '../../ui/exact';

export const RATE_MIN = -90;
export const RATE_MAX = 200;
export const MAX_STEPS = 4;

/** coefficient multiplicateur d'une évolution de t % */
export function coefficient(t: number): Frac {
  return frac(100 + t, 100);
}

/** t % de v (prendre t % d'une quantité = la multiplier par t/100) */
export function percentOf(v: number, t: number): ExactNumber {
  return exact(mul(frac(v), frac(t, 100)));
}

export interface Chain {
  /** valeurs successives V0, V1, … */
  values: ExactNumber[];
  /** coefficient global (produit) */
  global: ExactNumber;
  /** taux global en % (peut être décimal ou fraction) */
  globalRate: ExactNumber;
  /** taux réciproque en % pour revenir au départ, null si CM = 0 */
  reciprocalRate: ExactNumber | null;
  /** coefficient réciproque 1/CM */
  reciprocal: ExactNumber | null;
}

export function chain(start: number, rates: readonly number[]): Chain {
  let v: Frac = frac(start);
  let cm: Frac = frac(1);
  const values: ExactNumber[] = [exact(v)];
  for (const t of rates) {
    const c = coefficient(t);
    v = mul(v, c);
    cm = mul(cm, c);
    values.push(exact(v));
  }
  const rate = mul(sub(cm, frac(1)), frac(100));
  const inv = cm.p === 0 ? null : div(frac(1), cm);
  return {
    values,
    global: exact(cm),
    globalRate: exact(rate),
    reciprocal: inv ? exact(inv) : null,
    reciprocalRate: inv ? exact(mul(sub(inv, frac(1)), frac(100))) : null,
  };
}

/** « +20 % », « −15 % » */
export function rateLabel(t: number): string {
  return t >= 0 ? `+${t} %` : `−${-t} %`;
}

/** « × 1,2 », « × 0,85 » (coefficient d'un taux entier : décimal exact) */
export function coefficientLabel(t: number): string {
  return `× ${exact(coefficient(t)).plain}`;
}
